import { useState } from 'react';
import { C, SERIF, mono } from '../../theme';
import { Icon } from '../../icons';
import { Loading, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../api';
import type { CollectionRow } from '../../api';
import { useAsync } from '../../useAsync';
import { inr, fmtDate } from '../../format';
import { openWhatsApp, rentReminder } from '../../whatsapp';
import { Chip } from '../../components/Chip';
import type { ChipTone } from '../../components/Chip';

const RISK_LABEL = { new: 'New', low: 'Low', watch: 'Watch', high: 'High' } as const;
const MONTH_LETTER = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

function riskTone(risk: CollectionRow['risk']): ChipTone {
  // 'new' is an absence of a verdict, so it reads neutral, not good.
  if (risk === 'new') return 'neutral';
  return risk === 'high' ? 'overdue' : risk === 'watch' ? 'due' : 'paid';
}
function barColor(risk: CollectionRow['risk']): string {
  if (risk === 'new') return C.muted;
  return risk === 'high' ? C.red : risk === 'watch' ? C.amber : C.hi;
}
function standing(r: CollectionRow): { label: string; tone: ChipTone } {
  if (r.outstanding <= 0) return { label: 'Clear', tone: 'paid' };
  // With no settled history, state what is open rather than calling it overdue:
  // an unrecorded payment and an unpaid one look identical from here.
  if (r.risk === 'new') {
    const n = r.openPeriods;
    return { label: n === 1 ? '1 month open' : `${n} months open`, tone: 'due' };
  }
  if (r.currentOverdueDays > 0) return { label: `${r.currentOverdueDays}d overdue`, tone: 'overdue' };
  const di = r.dueInDays ?? 0;
  return { label: di === 0 ? 'Due today' : `Due in ${di}d`, tone: 'due' };
}
const HIST_COLOR = { onTime: C.hi, late: C.amber, unpaid: C.red } as const;

/**
 * Collections — every tenant's payment reliability, ranked worst-first, with a
 * one-tap WhatsApp nudge. Selecting a row opens its history and KPIs below.
 */
export function CollectionsPage({ propertyName }: { propertyName?: string }) {
  const notify = useToast();
  const { data, loading, error, reload } = useAsync<CollectionRow[]>(() => api.collections(), []);
  const [selId, setSelId] = useState<string | null>(null);

  if (loading && !data) return <Loading label="Loading collections…" minHeight={420} />;
  if (error) {
    return (
      <Empty icon="rupee" title="Collections unavailable" hint="We couldn't load reliability." minHeight={420}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  }
  const rows = data ?? [];
  if (rows.length === 0) {
    return <Empty icon="rupee" title="No active tenancies" hint="Reliability appears once tenants are on the books and paying." minHeight={420} />;
  }

  // Only tenancies with settled history can contribute to a portfolio rate;
  // otherwise one brand-new tenancy drags the whole property to 0%.
  const judged = rows.filter((r) => r.risk !== 'new');
  const totalMonths = judged.reduce((s, r) => s + r.monthsTotal, 0);
  const onTimeMonths = judged.reduce((s, r) => s + r.monthsOnTime, 0);
  const portfolioPct = totalMonths ? Math.round((onTimeMonths / totalMonths) * 100) : null;
  const high = rows.filter((r) => r.risk === 'high').length;
  const watch = rows.filter((r) => r.risk === 'watch').length;
  const outstanding = rows.reduce((s, r) => s + r.outstanding, 0);
  const sel = rows.find((r) => r.tenancyId === selId) ?? rows[0];

  function nudge(r: CollectionRow) {
    const amount = r.outstanding > 0 ? r.outstanding : r.rentAmount;
    const msg = rentReminder({
      property: propertyName ?? 'your property',
      tenant: r.tenantName,
      amount: inr(amount),
      dueLabel: r.dueDate ? fmtDate(r.dueDate) : 'the due date',
      overdue: r.currentOverdueDays > 0,
    });
    if (!openWhatsApp(r.tenantPhone, msg)) notify('Add the tenant’s phone (Invite tenant) to send on WhatsApp');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 3px', fontWeight: 600 }}>Collections</h2>
        <div style={{ color: C.muted, fontSize: 13.5 }}>Who's reliable, who's slipping. Ranked by risk, one tap to nudge.</div>
      </div>

      {/* health strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        <div style={stat}><div style={statK}>On-time rate · portfolio</div><div style={{ ...statV, color: portfolioPct == null ? C.muted : undefined, fontSize: portfolioPct == null ? 15 : undefined }}>{portfolioPct == null ? 'No history yet' : `${portfolioPct}%`}</div></div>
        <div style={stat}><div style={statK}>Tenants at risk</div><div style={{ ...statV, color: high ? C.red : C.hi }}>{high} <span style={{ fontSize: 15, color: C.muted, fontWeight: 400 }}>high · {watch} watch</span></div></div>
        <div style={stat}><div style={statK}>Outstanding</div><div style={{ ...statV, color: outstanding > 0 ? C.red : C.hi }}>{inr(outstanding)}</div></div>
      </div>

      {/* table */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: `1px solid ${C.line2}` }}>
          <h3 style={{ fontSize: 15, margin: 0, fontWeight: 650 }}>Tenants · reliability</h3>
          <span style={{ fontSize: 12.5, color: C.muted }}>Needs attention first</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.4 }}>
            <thead>
              <tr>
                {['Tenant', 'Standing', 'On-time history', 'Avg late', 'Risk', 'Outstanding', ''].map((h, i) => (
                  <th key={i} style={{ textAlign: i >= 3 && i <= 5 ? 'right' : 'left', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, fontWeight: 600, padding: '10px 16px 9px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = standing(r);
                const on = sel.tenancyId === r.tenancyId;
                return (
                  <tr key={r.tenancyId} onClick={() => setSelId(r.tenancyId)} style={{ cursor: 'pointer', background: on ? C.soft : 'transparent' }}>
                    <td style={{ ...td, borderLeft: `3px solid ${on ? C.accent : 'transparent'}` }}>
                      <b style={{ color: C.ink, fontWeight: 600 }}>{r.tenantName}</b>
                      <small style={{ display: 'block', color: C.muted, fontSize: 11.5 }}>{r.unitLabel}</small>
                    </td>
                    <td style={td}><Chip tone={st.tone}>{st.label}</Chip></td>
                    <td style={td}>
                      {r.risk === 'new' ? (
                        <span style={{ fontSize: 12, color: C.muted }}>Not enough history</span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <span style={{ width: 74, height: 7, borderRadius: 99, background: C.line2, overflow: 'hidden', flex: 'none' }}><span style={{ display: 'block', height: '100%', width: `${r.onTimePct}%`, background: barColor(r.risk) }} /></span>
                          <span style={{ ...mono(), fontSize: 12, color: C.muted }}>{r.monthsOnTime}/{r.monthsTotal} · {r.onTimePct}%</span>
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', ...mono(), color: r.risk === 'new' ? C.muted : undefined }}>{r.risk === 'new' ? '–' : r.avgDaysLate}</td>
                    <td style={{ ...td, textAlign: 'right' }}><Chip tone={riskTone(r.risk)}>{RISK_LABEL[r.risk]}</Chip></td>
                    <td style={{ ...td, textAlign: 'right', ...mono(), color: r.outstanding > 0 ? C.ink : C.muted }}>{r.outstanding > 0 ? inr(r.outstanding) : '–'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {r.risk === 'low' && r.outstanding <= 0 ? (
                        <span style={{ fontSize: 12, color: C.muted }}>–</span>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); nudge(r); }} title={r.tenantPhone ? 'Open WhatsApp with a reminder' : 'No phone on file'} style={{ ...nudgeBtn, opacity: r.tenantPhone ? 1 : 0.5 }}>
                          <Icon n="chat" size={13} color="#fff" /> Nudge
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* selected detail */}
      {sel && <Detail r={sel} onNudge={() => nudge(sel)} />}

      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        Reliability is measured from recorded payments vs each month's due date. <b>New</b> means fewer than two months have settled either way, so there is nothing to judge yet. <b>Risk</b>: High when 30+ days overdue or on-time under 60%; Watch at 7–30 days or 60–85%; else Low.
      </div>
    </div>
  );
}

function Detail({ r, onNudge }: { r: CollectionRow; onNudge: () => void }) {
  const hist = r.history.slice(-12);
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 600 }}>{r.tenantName} <span style={{ fontFamily: 'inherit', fontSize: 12.5, color: C.muted, fontWeight: 400 }}>· {r.unitLabel}</span></div>
          <div style={{ marginTop: 6 }}><Chip tone={riskTone(r.risk)}>{r.risk === 'new' ? 'New tenancy' : `${RISK_LABEL[r.risk]} risk`}</Chip></div>
        </div>
        <button onClick={onNudge} title={r.tenantPhone ? 'Open WhatsApp with a reminder' : 'No phone on file'} style={{ ...nudgeBtn, padding: '9px 15px', fontSize: 13.5, opacity: r.tenantPhone ? 1 : 0.5 }}>
          <Icon n="chat" size={15} color="#fff" /> Send reminder on WhatsApp
        </button>
      </div>

      <div style={{ display: 'flex', gap: 28, margin: '16px 0 14px', flexWrap: 'wrap' }}>
        {([['On-time', r.risk === 'new' ? '–' : `${r.onTimePct}%`], ['Avg days late', r.risk === 'new' ? '–' : String(r.avgDaysLate)], ['Months tracked', String(r.monthsTotal)], ['Outstanding', r.outstanding > 0 ? inr(r.outstanding) : '₹0']] as const).map(([k, v]) => (
          <div key={k}><div style={{ fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, fontWeight: 600 }}>{k}</div><div style={{ ...mono(), fontSize: 18, fontWeight: 650, marginTop: 3 }}>{v}</div></div>
        ))}
      </div>

      <div style={{ fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, fontWeight: 600, marginBottom: 8 }}>Payment history · last {hist.length} months</div>
      {hist.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.muted }}>No history yet.</div>
      ) : (
        <div style={{ display: 'flex', gap: 5 }}>
          {hist.map((h, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div title={`${h.period} · ${h.status}`} style={{ height: 24, borderRadius: 5, background: HIST_COLOR[h.status] + '33', border: `1px solid ${HIST_COLOR[h.status]}` }} />
              <small style={{ fontSize: 9, color: C.muted, ...mono(), display: 'block', marginTop: 4 }}>{MONTH_LETTER[Number(h.period.split('-')[1]) - 1]}</small>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: C.muted }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: C.hi, display: 'inline-block' }} /> on time</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: C.amber, display: 'inline-block' }} /> late</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 10, height: 10, borderRadius: 3, background: C.red, display: 'inline-block' }} /> unpaid</span>
      </div>
    </div>
  );
}

const stat: React.CSSProperties = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: '13px 15px' };
const statK: React.CSSProperties = { fontSize: 11, letterSpacing: '.04em', textTransform: 'uppercase', color: C.muted, fontWeight: 600 };
const statV: React.CSSProperties = { fontFamily: SERIF, fontSize: 26, fontWeight: 600, marginTop: 4 };
const td: React.CSSProperties = { padding: '12px 16px', borderTop: `1px solid ${C.line2}`, verticalAlign: 'middle' };
const nudgeBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#128c4b', color: '#fff', border: 0, borderRadius: 8, padding: '7px 12px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' };
const retryBtn: React.CSSProperties = { marginTop: 4, border: `1px solid ${C.line}`, background: C.card, color: C.accent, fontWeight: 600, fontSize: 13, padding: '8px 16px', borderRadius: 9, cursor: 'pointer' };
